import { ActionButtonProps } from '@euler/app/components/BottomAction';
import {
  iPadPageSlot,
  PageContainerForIpad,
} from '@euler/app/components/PageContainerForIpad';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useDeliveryCheckContext } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckContext';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { OrgMember } from '@euler/model/entity';
import { DeliveryCheckSceneType } from '@euler/model/enum';
import { DeliveryCheckItemStaged } from '@euler/model/task-detail';
import { showAlert } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ListRenderItem,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Fill } from 'react-slot-fill';
import { BehaviorSubject, Subject } from 'rxjs';
import { useDeliveryCheck } from '../functions/useDeliveryCheck';
import { useDeliveryCheckMediaController } from '../functions/useDeliveryCheckMediaController';
import { useDeliveryCheckScrollView } from '../functions/useDeliveryCheckScrollView';
import { useDeliveryCheckUserPicker } from '../functions/useDeliveryCheckUserPicker';
import {
  InvalidDeliveryCheckItemHandler,
  validateDeliveryCheckItem,
} from '../functions/util';
import { DeliveryCheckCell } from './DeliveryCheckCell';
import { DeliveryCheckUserBottomSheetModal } from './DeliveryCheckUserBottomSheetModal';

type ActionType = 'save' | 'preview';

const kPageId = 'deliverycheck';

export const WiredDeliveryCheckScreenContentForIpad = memo(() => {
  const isLandscape = useIsLandscape();
  const { rpx } = useResponsiveContext();

  const { detail, profile } = useTaskContext();
  const { manager } = useDeliveryCheckContext();

  const action$ = useCreation(() => new Subject<ActionType | undefined>(), []);

  const progress$ = useCreation(() => new BehaviorSubject<string>(''), []);

  const [progress] = useBehaviorSubject(progress$);

  const actions = useMemo(() => {
    const res: ActionButtonProps[] = [
      {
        bordered: true,
        text: '保存',
        variant: 'white',
        onPress: () => action$.next('save'),
      },
      {
        bordered: true,
        text: '预览报告',
        variant: 'white',
        onPress: () => action$.next('preview'),
        style: { marginLeft: rpx(15) },
      },
    ];
    return res;
  }, [action$, rpx]);

  const pageTitle = profile.store.isIndependentDeliveryCheckEnabled
    ? manager.sceneType === DeliveryCheckSceneType.CompletionCheck
      ? '竣工检验'
      : '完工抽检'
    : '交车检查';

  return (
    <>
      <PageContainerForIpad
        name={kPageId}
        title={pageTitle}
        subTitle={progress}
        actions={actions}
        headerLeftContainerStyle={{ backgroundColor: 'green', flex: 1 }}
        titleContainerStyle={{}}
        contentContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
        headerStyle={
          isLandscape
            ? { paddingTop: rpx(15), paddingBottom: rpx(15) }
            : undefined
        }
        footerStyle={isLandscape ? { paddingTop: rpx(15) } : undefined}
      >
        <Content action$={action$} progress$={progress$} />

        <Fill name={iPadPageSlot(kPageId, 'header/right')}>
          <View
            css={`
              align-items: flex-end;
            `}
          >
            <Typography.Label
              bold
              size={rpx(28)}
              color={'white'}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.licensePlateNo}
            </Typography.Label>
            <Typography.Label
              regular
              size={rpx(16)}
              color={Colors.White4}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {detail.basicInfo.vehicleName}
            </Typography.Label>
          </View>
        </Fill>
      </PageContainerForIpad>
    </>
  );
});

const kCellMargin = 20;

const Content = memo(
  ({
    action$,
    progress$,
  }: {
    action$: Subject<ActionType | undefined>;
    progress$: BehaviorSubject<string>;
  }) => {
    const { width, height } = useContainerLayout();
    const { rpx } = useResponsiveContext();
    const {
      taskManager: { currentUser: profile },
    } = useTaskContext();

    const cellMargin = rpx(kCellMargin);

    const [technician, setTechnician] = useState<{ id: number; name: string }>({
      id: profile.user.memberId,
      name: profile.user.name,
    });

    const {
      isUserPickerVisible,
      onUserPickerDismiss,
      onUserSelected,
      onUserPickerPress,
    } = useDeliveryCheckUserPicker();

    const onTechnicianChange = useCallback(
      (tech: OrgMember) => {
        setTechnician({ id: tech.id, name: tech.name });
        onUserSelected(tech);
      },
      [onUserSelected],
    );

    const {
      scrollViewRef,
      scrollToItemAnimated,
      onCellRef,
      onCellLayout,
      onScroll,
    } = useDeliveryCheckScrollView({
      minPos: 0,
      maxPos: width,
      horizontal: true,
    });

    const onInvalidItem: InvalidDeliveryCheckItemHandler = useCallback(
      ({ item, index, error, message }) => {
        scrollToItemAnimated({ key: item._key, index }, () => {
          if (error === 'unexpected-option') {
            void showAlert('提示', message);
          }
        });
      },
      [scrollToItemAnimated],
    );

    const onNewWorkItems = useCallback(
      (items: { key: string; index: number }[]) => {
        if (!items.length) return;
        scrollToItemAnimated({ key: items[0].key, index: items[0].index });
      },
      [scrollToItemAnimated],
    );

    const {
      staged,
      manager,
      sceneType,
      errorItemKey,
      changed$,
      onRemarkTextChange,
      onOptionCheckChange,
      onRemove,
      onSave,
      onConfirm,
      onEditUnacceptedReason,
      onViewReworkRecords,
      onEditItemReworkRecord,
      onRemoveItemReworkRecord,
    } = useDeliveryCheck({ onInvalidItem, onNewWorkItems });

    const progress = useMemo(() => {
      const total = staged.items.length;
      const checked = staged.items.filter(
        x => validateDeliveryCheckItem(sceneType, x) == null,
      ).length;
      return `${checked}/${total}`;
    }, [sceneType, staged.items]);

    useEffect(() => {
      progress$.next(progress);
    }, [progress, progress$]);

    const {
      onTakeMedia,
      onEditMedia,
      onDeleteMedia,
      onPreviewVideo,
      onRetryMediaUpload,
    } = useDeliveryCheckMediaController({ changed$ });

    useEffect(() => {
      // always use list mode for ipad view
      manager.setMode('list');
    }, [manager]);

    useEffect(() => {
      const sub = action$.subscribe(action => {
        if (action === 'save') {
          void onSave();
        } else if (action === 'preview') {
          void onConfirm();
        }
      });
      return () => sub.unsubscribe();
    }, [action$, onConfirm, onSave]);

    const isLandscape = useIsLandscape();
    const columns = isLandscape ? 2.35 : 1.35;
    const [cellWidth] = computeGridLayoutItemSize({
      numberOfColumns: columns,
      gridWidth: width,
      cellMargin: { left: cellMargin, top: 0, bottom: 0, right: cellMargin },
      aspectRatio: 5 / 4,
      gridPadding: 0,
    });

    const cellStyle = useMemo<StyleProp<ViewStyle>>(() => {
      return isLandscape
        ? {
            height: height - rpx(80),
            paddingBottom: rpx(10),
          }
        : {
            height: (height * 2) / 3,
          };
    }, [height, isLandscape, rpx]);

    const renderItem: ListRenderItem<DeliveryCheckItemStaged> = usePersistFn(
      ({ item, index }) => {
        return (
          <DeliveryCheckCell
            key={item._key}
            item={item}
            mode={'list'}
            index={index}
            scrollable={true}
            ref={ref => onCellRef(item._key, ref)}
            alwaysShowControls
            showErrorIndicators={errorItemKey === item._key}
            removeButtonPlacement="outside"
            removeButtonSize={rpx(32)}
            total={staged.items.length}
            onTakeMedia={onTakeMedia}
            onDeleteMedia={onDeleteMedia}
            onEditMedia={onEditMedia}
            onRetryMediaUpload={onRetryMediaUpload}
            onPreviewVideo={onPreviewVideo}
            onRemarkChange={onRemarkTextChange}
            onOptionCheckChange={onOptionCheckChange}
            onLayout={onCellLayout}
            onRemove={onRemove}
            onEditUnacceptedReason={onEditUnacceptedReason}
            onEditItemReworkRecord={onEditItemReworkRecord}
            onRemoveItemReworkRecord={onRemoveItemReworkRecord}
            style={cellStyle}
            titleStyle={{
              fontFamily: FontFamily.NotoSans.Medium,
              fontSize: rpx(22),
              color: '#444',
            }}
            optionListStyle={{
              marginTop: isLandscape ? rpx(-5) : 0,
            }}
            mediaRemarkContainerStyle={{
              flex: 1,
              marginTop: isLandscape ? rpx(-10) : 0,
            }}
            remarkStyle={{ flex: 1 }}
            css={`
              margin: 0 ${cellMargin}px;
              width: ${cellWidth}px;
              border-width: ${rpx(3)}px;
            `}
          />
        );
      },
    );

    const getItemLayout = useCallback(
      (_: any, index: number) => {
        return {
          length: cellWidth + 2 * cellMargin,
          offset: (cellWidth + 2 * cellMargin) * index,
          index,
        };
      },
      [cellMargin, cellWidth],
    );

    return (
      <>
        <FlatList
          data={staged.items}
          keyExtractor={x => x._key}
          ref={scrollViewRef as any}
          renderItem={renderItem}
          // snapToInterval={cellWidth + cellMargin * 2}
          // snapToAlignment="center"
          // decelerationRate="fast"
          onScroll={onScroll}
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          getItemLayout={getItemLayout}
          contentContainerStyle={{
            alignItems: 'center',
            paddingLeft: rpx(20),
            paddingRight: rpx(20),
          }}
        />
        <Fill name={iPadPageSlot(kPageId, 'footer/left')}>
          <StackLayout direction="row" alignItems="center" gap={rpx(15)}>
            <TouchableOpacity
              onPress={onUserPickerPress}
              css={`
                flex-direction: row;
                align-items: center;
              `}
            >
              <Typography.Label
                light
                color={Colors.White7_5}
                size={rpx(18)}
                lineHeight={rpx(24)}
              >
                质检员: {technician.name}
              </Typography.Label>
              <Icon
                name="pencil"
                color={Colors.White7_5}
                size={rpx(12)}
                css={`
                  margin-left: ${rpx(8)}px;
                `}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onViewReworkRecords}
              css={`
                margin-left: ${rpx(15)}px;
              `}
            >
              <Typography.Label medium color="#fff" size={rpx(18)} light>
                内返记录
              </Typography.Label>
            </TouchableOpacity>
          </StackLayout>
        </Fill>
        <DeliveryCheckUserBottomSheetModal
          visible={isUserPickerVisible}
          onDismiss={onUserPickerDismiss}
          userId={technician.id}
          onSelect={onTechnicianChange}
        />
      </>
    );
  },
);
